import { useEffect, useRef } from 'react';

/**
 * Custom hook for Google Places Autocomplete
 * @param {Object} options - Autocomplete options
 * @param {Function} onPlaceSelect - Callback when a place is selected
 * @returns {Object} - Reference to attach to input element
 */
export function useGooglePlacesAutocomplete(options = {}, onPlaceSelect) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    let autocompleteObj = null;
    let listener = null;
    let pollHandle = null;

    const initializeIfReady = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) return false;

      try {
        // Initialize legacy Autocomplete
        autocompleteObj = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address', 'geometry'],
          ...options,
        });

        listener = autocompleteObj.addListener('place_changed', () => {
          const place = autocompleteObj.getPlace();
          handlePlace(place);
        });

        autocompleteRef.current = autocompleteObj;
        return true;
      } catch (err) {
        console.error('Google Places autocomplete initialization failed:', err);
        return false;
      }
    };

    // If google is already available, initialize immediately
    if (!initializeIfReady()) {
      // Poll for the maps script to load (simple, short-lived polling)
      let attempts = 0;
      pollHandle = setInterval(() => {
        attempts += 1;
        if (initializeIfReady() || attempts > 20) {
          clearInterval(pollHandle);
          pollHandle = null;
        }
      }, 300);
    }

    // Helper to parse place result into our parsedAddress shape
    const handlePlace = (place) => {
      if (!place || !place.address_components) return;

      const addressComponents = {};
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          addressComponents.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          addressComponents.route = component.long_name;
        }
        if (types.includes('locality')) {
          addressComponents.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          addressComponents.state = component.short_name;
        }
        if (types.includes('postal_code')) {
          addressComponents.zipCode = component.long_name;
        }
        if (types.includes('country')) {
          addressComponents.country = component.short_name;
        }
      });

      const street = [addressComponents.streetNumber, addressComponents.route].filter(Boolean).join(' ');

      const parsedAddress = {
        street: street || '',
        city: addressComponents.city || '',
        state: addressComponents.state || '',
        zipCode: addressComponents.zipCode || '',
        country: addressComponents.country || 'US',
        formattedAddress: place.formatted_address || '',
        latitude: place.geometry?.location?.lat() || null,
        longitude: place.geometry?.location?.lng() || null,
      };

      if (onPlaceSelect) onPlaceSelect(parsedAddress);
    };

    try {
      // Use legacy Autocomplete class (still supported, receives bug fixes)
      // Note: PlaceAutocompleteElement provides its own input field and cannot be
      // used with existing React controlled inputs, so we stick with Autocomplete.
      autocompleteObj = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry'],
        ...options,
      });

      listener = autocompleteObj.addListener('place_changed', () => {
        const place = autocompleteObj.getPlace();
        handlePlace(place);
      });
    } catch (err) {
      // If initialization fails, degrade gracefully (input remains usable)
      console.error('Google Places autocomplete initialization failed:', err);
    }

    // Prevent form submission on Enter when dropdown is visible
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && document.querySelector('.pac-container')) {
        const firstItem = document.querySelector('.pac-item:first-child');
        if (firstItem) {
          e.preventDefault();
        }
      }
    };

  input.addEventListener('keydown', handleKeyDown);

    // Store reference for potential external use
    autocompleteRef.current = autocompleteObj;

    // Cleanup
    return () => {
      input.removeEventListener('keydown', handleKeyDown);
      if (pollHandle) {
        clearInterval(pollHandle);
      }
      try {
        if (listener && window.google && window.google.maps && window.google.maps.event && typeof window.google.maps.event.removeListener === 'function') {
          window.google.maps.event.removeListener(listener);
        } else if (listener && typeof listener.remove === 'function') {
          // Some listeners return an object with remove()
          listener.remove();
        }
      } catch (err) {
        // ignore cleanup errors
      }
    };
  }, [onPlaceSelect, options]);

  return inputRef;
}
